import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X as XIcon, Loader2, Camera, Wrench, AlertTriangle, Trash2 } from 'lucide-react';
import { fileToUploadB64 } from './driverUtils';

export const REPORT_TYPES = [
  { key: 'defect', label: 'Vehicle defect', icon: Wrench },
  { key: 'issue', label: 'Issue', icon: AlertTriangle },
];

const OFF_ROAD = ['vehicle_damage', 'breakdown', 'vehicle_breakdown'];
const FIELD = 'w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-base';
const Label = ({ children }) => <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">{children}</div>;

const PhotoPicker = ({ photos, setPhotos, label }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - photos.length);
    setBusy(true);
    const out = [];
    for (const f of files) out.push(await fileToUploadB64(f));
    setPhotos([...photos, ...out].slice(0, 4));
    setBusy(false);
    if (ref.current) ref.current.value = '';
  };
  return (
    <div>
      <Label>{label} ({photos.length}/4)</Label>
      <div className="flex gap-2 flex-wrap">
        {photos.map((p, i) => (
          <div key={i} className="relative">
            <img src={p} alt={`upload ${i + 1}`} className="w-16 h-16 object-cover rounded-md border border-slate-200" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
            <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-slate-900 text-white inline-flex items-center justify-center" aria-label="Remove photo" data-testid={`report-photo-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {photos.length < 4 && (
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="w-16 h-16 rounded-md border-2 border-dashed border-slate-300 text-slate-500 inline-flex items-center justify-center" data-testid="report-photo-add">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={onFiles} data-testid="report-photo-input" />
    </div>
  );
};

export const ReportModal = ({ authFetch, jobs, categories, initialType = 'defect', onClose, onCreated }) => {
  const [type, setType] = useState(initialType);
  const [category, setCategory] = useState('');
  const [vans, setVans] = useState(null);
  const [vanId, setVanId] = useState('');
  const [quoteId, setQuoteId] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { authFetch('get', '/vans').then(({ data }) => setVans(data.vans || [])).catch(() => {}); }, [authFetch]);
  useEffect(() => { setCategory(''); }, [type]);

  const cats = Object.entries(categories?.[type] || {});
  const submit = async () => {
    if (!category) return toast.error('Pick a category.');
    if (type === 'defect' && !vanId) return toast.error('Choose which van has the problem.');
    if (type === 'expense' && !(parseFloat(amount) > 0)) return toast.error('Enter the amount.');
    if (type !== 'expense' && !description.trim() && !photos.length) return toast.error('Add a short description or a photo.');
    setSaving(true);
    try {
      const { data } = await authFetch('post', '/driver/reports', {
        type, category, description, photos,
        van_id: vanId || null, quote_id: quoteId || null,
        amount: type === 'expense' ? parseFloat(amount) : null,
      });
      toast.success(data.report.van_marked_off_road ? `Report #${data.report.ref} sent · van marked off road.` : `Report #${data.report.ref} sent to the office.`);
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send report.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="report-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 px-4 pt-4 pb-2 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl font-extrabold text-[color:var(--mv-navy)]">Report</h3>
            <button type="button" onClick={onClose} className="w-11 h-11 inline-flex items-center justify-center text-slate-500" aria-label="Close" data-testid="report-close"><XIcon className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-3 gap-1 bg-slate-100 rounded-lg p-1 mt-1">
            {REPORT_TYPES.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.key} type="button" onClick={() => setType(t.key)} className={`h-11 rounded-md text-sm font-bold inline-flex items-center justify-center gap-1.5 transition-colors ${type === t.key ? 'bg-white shadow text-[color:var(--mv-navy)]' : 'text-slate-500'}`} data-testid={`report-tab-${t.key}`}>
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <Label>Category *</Label>
            <div className="grid grid-cols-2 gap-2">
              {cats.map(([k, label]) => (
                <button key={k} type="button" onClick={() => setCategory(k)} className={`min-h-[44px] px-3 py-2 rounded-md text-sm font-bold text-left border transition-colors ${category === k ? 'bg-[color:var(--mv-navy)] text-white border-[color:var(--mv-navy)]' : 'bg-white text-slate-700 border-slate-200'}`} data-testid={`report-cat-${k}`}>
                  {label}
                </button>
              ))}
            </div>
            {OFF_ROAD.includes(category) && vanId && <p className="mt-2 text-xs font-bold text-red-700" data-testid="report-offroad-note">This will mark the van OFF ROAD until the office clears it.</p>}
          </div>
          {type !== 'expense' && (
            <div>
              <Label>Van {type === 'defect' ? '*' : '(optional)'}</Label>
              <select value={vanId} onChange={(e) => setVanId(e.target.value)} className={FIELD} data-testid="report-van">
                <option value="">{vans === null ? 'Loading vans…' : vans.length ? 'Choose a van…' : 'No vans set up yet — ask the office'}</option>
                {(vans || []).map(v => <option key={v.id} value={v.id}>{v.reg}{v.name ? ` · ${v.name}` : ''}{v.off_road ? ' (off road)' : ''}</option>)}
              </select>
            </div>
          )}
          {type === 'expense' && (
            <div>
              <Label>Amount (£) *</Label>
              <input type="number" min="0" step="0.01" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={FIELD} placeholder="0.00" data-testid="report-amount" />
            </div>
          )}
          <div>
            <Label>Job (optional)</Label>
            <select value={quoteId} onChange={(e) => setQuoteId(e.target.value)} className={FIELD} data-testid="report-job">
              <option value="">Not linked to a job</option>
              {jobs.map(j => <option key={j.id} value={j.id}>#{j.ref} · {j.booking?.date} {j.booking?.time} · {j.name}</option>)}
            </select>
          </div>
          <div>
            <Label>{type === 'expense' ? 'Note (optional)' : 'What happened?'}</Label>
            <textarea rows={3} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-2 text-base" placeholder={type === 'expense' ? 'e.g. Diesel at Shell, A13' : 'Describe the problem…'} data-testid="report-description" />
          </div>
          <PhotoPicker photos={photos} setPhotos={setPhotos} label={type === 'expense' ? 'Receipt photo' : 'Photos'} />
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
          <button type="button" onClick={submit} disabled={saving} className="w-full h-12 rounded-md bg-[color:var(--mv-orange)] hover:bg-orange-600 text-white font-black inline-flex items-center justify-center gap-2" data-testid="report-submit">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Send to office
          </button>
        </div>
      </div>
    </div>
  );
};
